import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, User, Users } from 'lucide-react';
import { useQuest } from '@/context/QuestContext';
import { CLARITY_LEVELS } from '@/lib/quizData';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { track } from '@/lib/api';
import { BrandBanner } from '@/components/BrandLogo';

const GRADES = ['8', '9', '10', '11', '12'];
const ROLES = [
  { id: 'parent', label: 'Parent', sub: 'I am filling this on my child\'s behalf', Icon: Users },
  { id: 'student', label: 'Student', sub: 'I am taking this for myself', Icon: User },
];

export default function PlayerSetup() {
  const navigate = useNavigate();
  const { session, setSession } = useQuest();

  const [form, setForm] = useState({
    student_name: session.player?.student_name || '',
    grade: session.player?.grade || '',
    parent_name: session.player?.parent_name || '',
    parent_whatsapp: session.player?.parent_whatsapp || '',
    parent_email: session.player?.parent_email || '',
    city: session.player?.city || '',
    clarity_level: session.player?.clarity_level || 0,
    role: session.player?.role || '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e?.preventDefault();
    const required = ['student_name', 'grade', 'parent_name', 'parent_whatsapp', 'parent_email', 'city', 'clarity_level', 'role'];
    for (const k of required) {
      if (!form[k]) {
        toast.error('Please complete all fields to continue');
        return;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.parent_email)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (form.parent_whatsapp.replace(/\D/g, '').length < 8) {
      toast.error('Please enter a valid WhatsApp number');
      return;
    }
    // Backend still expects self_archetype in the LeadInput schema (kept optional).
    // We pass role as the primary signal and derive a neutral default archetype.
    const player = { ...form, self_archetype: form.role === 'student' ? 'Explorer' : 'Guide' };
    setSession({ ...session, player });
    track('quiz_setup_completed', { grade: form.grade, clarity: form.clarity_level, role: form.role });
    navigate('/intro');
  };

  return (
    <div className="min-h-screen gradient-bg grain">
      <BrandBanner />
      <nav className="sticky top-0 z-30 glass">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" data-testid="back-to-landing" className="flex items-center gap-2 text-sm font-bold text-muted-ink hover:text-ink">
            <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back
          </Link>
          <div className="font-display font-extrabold text-ink text-sm">Your Profile</div>
          <div className="w-12" />
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0055FF]">Step 1 of 2</p>
          <h1 className="font-display font-black text-ink text-3xl sm:text-4xl mt-2 tracking-tight">Create Your Career Fit Assessment Profile</h1>
          <p className="text-body mt-2">Before we start, tell us a little about you.</p>
        </motion.div>

        <form onSubmit={submit} data-testid="player-setup-form" className="mt-8 space-y-6">
          <div className="card-soft p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Student name">
                <Input data-testid="input-student-name" value={form.student_name} onChange={(e) => set('student_name', e.target.value)} placeholder="e.g. Aanya" className="rounded-2xl h-12" />
              </Field>
              <Field label="Grade">
                <div className="flex gap-2 flex-wrap">
                  {GRADES.map((g) => (
                    <button
                      type="button"
                      key={g}
                      data-testid={`grade-${g}`}
                      onClick={() => set('grade', g)}
                      className={`h-12 px-4 rounded-2xl border-2 font-display font-extrabold text-sm transition-all ${form.grade === g ? 'border-[#0055FF] bg-[#0055FF] text-white' : 'border-[#E2E8F0] bg-white text-ink hover:border-[#0055FF]'}`}
                    >Grade {g}</button>
                  ))}
                </div>
              </Field>
              <Field label="Parent name">
                <Input data-testid="input-parent-name" value={form.parent_name} onChange={(e) => set('parent_name', e.target.value)} placeholder="e.g. Priya" className="rounded-2xl h-12" />
              </Field>
              <Field label="Parent WhatsApp number">
                <Input data-testid="input-parent-whatsapp" value={form.parent_whatsapp} onChange={(e) => set('parent_whatsapp', e.target.value)} placeholder="+91 98xxxxxxxx" className="rounded-2xl h-12" />
              </Field>
              <Field label="Parent email">
                <Input data-testid="input-parent-email" type="email" value={form.parent_email} onChange={(e) => set('parent_email', e.target.value)} placeholder="parent@email.com" className="rounded-2xl h-12" />
              </Field>
              <Field label="City">
                <Input data-testid="input-city" value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="e.g. Bengaluru" className="rounded-2xl h-12" />
              </Field>
            </div>
          </div>

          <div className="card-soft p-6 sm:p-8">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-ink">Current career clarity level</Label>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {CLARITY_LEVELS.map((c) => (
                <button
                  type="button"
                  key={c.value}
                  data-testid={`clarity-${c.value}`}
                  onClick={() => set('clarity_level', c.value)}
                  className={`text-left p-4 rounded-2xl border-2 transition-all ${form.clarity_level === c.value ? 'border-[#0055FF] bg-[#0055FF]/5' : 'border-[#E2E8F0] bg-white hover:border-[#0055FF]'}`}
                >
                  <div className="font-display font-extrabold text-ink text-sm">{c.value}. {c.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="card-soft p-6 sm:p-8">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-ink">Are you a parent or student?</Label>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {ROLES.map((r) => {
                const active = form.role === r.id;
                const Icon = r.Icon;
                return (
                  <button
                    type="button"
                    key={r.id}
                    data-testid={`role-${r.id}`}
                    onClick={() => set('role', r.id)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all flex items-start gap-3 ${active ? 'border-[#0070fb] bg-[#0070fb]/5' : 'border-[#E2E8F0] bg-white hover:border-[#0070fb]'}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? 'bg-[#0070fb] text-white' : 'bg-[#0070fb]/10 text-[#0070fb]'}`}>
                      <Icon strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="font-display font-extrabold text-ink">{r.label}</div>
                      <div className="text-xs text-muted-ink">{r.sub}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <button type="submit" data-testid="enter-quest-button" className="btn-primary w-full inline-flex items-center justify-center gap-2">
            Start Assessment <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-ink">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
